import React, { ReactElement } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ResetPasswordIcon from "../../images/resetfinal.png";
import EditIcon from "../../images/edit-icon.png";
import { useEnumStrings } from "../enumStrings";

interface ResetDashboardPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSendEmail: () => void;
  onPreviewEmail: () => void;
  onEdit?: () => void;
  loading: boolean;
  contactName: string;
  contactEmail: string;
  title?: string;
  description?: string;
  variant?: "reset" | "resend" | "accessed";
  memberCompanyName?: string;
}

const ResetDashboardPasswordDialog = ({
  open,
  onClose,
  onSendEmail,
  onPreviewEmail: _onPreviewEmail,
  onEdit,
  loading,
  contactName,
  contactEmail,
  title,
  description,
  variant = "reset",
  memberCompanyName,
}: ResetDashboardPasswordDialogProps): ReactElement => {
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const isResend = variant === "resend";
  const isAccessed = variant === "accessed";
  const isWideVariant = isResend || isAccessed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: isMobile ? "16px" : "20px",
          padding: "0",
          maxWidth: isMobile ? "calc(100% - 16px)" : (isWideVariant ? "718px" : "580px"),
          width: "100%",
          margin: isMobile ? "8px" : undefined,
          maxHeight: isMobile ? "calc(100vh - 16px)" : undefined,
          overflow: "hidden",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        },
      }}
      maxWidth={false}
    >
      {/* Close button */}
      <IconButton
        style={{
          position: "absolute",
          top: isWideVariant ? "16px" : "8px",
          right: isWideVariant ? "16px" : "8px",
          zIndex: 1,
          backgroundColor: "#ED1C24",
          color: "#fff",
          width: "30px",
          height: "30px",
          padding: 0,
        }}
        onClick={onClose}
        aria-label="close"
      >
        <CloseIcon style={{ fontSize: "18px" }} />
      </IconButton>
      <DialogContent
        style={{
          padding: isMobile
            ? (isWideVariant ? "20px 14px 20px" : "36px 14px 20px")
            : (isWideVariant ? "24px 40px 36px" : "48px 40px 36px"),
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
        }}
      >
        {/* Icon */}
        {isWideVariant ? (
          <div
            style={{
              width: isMobile ? "72px" : "105px",
              height: isMobile ? "72px" : "105px",
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: "#E1A731",
              border: "1px solid #E1A731",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: isMobile ? "12px" : "16px",
            }}
          >
            <ErrorOutlineIcon style={{ fontSize: isMobile ? "42px" : "60px", color: "#FFFFFF" }} />
          </div>
        ) : (
          <img
            src={ResetPasswordIcon}
            alt="Reset Password"
            style={{
              width: isMobile ? 64 : 80,
              height: isMobile ? 64 : 80,
              display: "block",
              marginBottom: isMobile ? 14 : 20,
            }}
          />
        )}
        {/* Title */}
        <div
          style={{
            fontFamily: "DM Sans, sans-serif",
            fontWeight: 600,
            fontSize: isMobile ? "18px" : (isWideVariant ? "25px" : "22px"),
            lineHeight: isMobile ? "130%" : "200%",
            color: "#000000",
            marginBottom: isMobile ? "10px" : (isWideVariant ? "8px" : "16px"),
            textAlign: "center",
          }}
        >
          {title || (isAccessed
            ? "Member Dashboard has already been accessed."
            : isResend
            ? "Resend Credentials"
            : enumStrings.dialogs.resetDashboardPassword)}
        </div>
        {/* Description */}
        <div
          style={{
            fontFamily: "DM Sans, sans-serif",
            fontWeight: 400,
            fontSize: isMobile ? "13px" : (isWideVariant ? "18px" : "16px"),
            color: "#000000",
            lineHeight: isMobile ? "150%" : "200%",
            marginBottom: isMobile ? "14px" : "24px",
            textAlign: "center",
            maxWidth: isWideVariant ? "595px" : "360px",
            width: "100%",
          }}
        >
          {description || (isAccessed ? (<>
            <strong>{contactName || "{Insert Company Administrator Contact}"}</strong> has already accessed <strong>{memberCompanyName || "{Insert Company Name}"}</strong> member dashboard. If this contact requires a new password to access their member dashboard or the contact name and email has changed, go to <strong>Manage Members</strong> in your chamber dashboard to resend password or update contact information.
          </>) : isResend ? (<>
            <strong>{memberCompanyName || "{INSERT COMPANY NAME}"}</strong> has not accessed their Member Dashboard.
            <br /><br />
            Please confirm with the Company Administrator Contact to ensure the previous email did not go to spam. If the contact below has changed, click <strong>Edit Button</strong> to update new contact and a new set of credentials will be created and sent.
          </>) : (<>
            Please confirm the name and email below is correct before you click <strong>RESET</strong> button. If the contact below has changed, click <strong>Edit Button</strong> to update new contact and a new set of credentials will be created.
          </>))}
        </div>
        {/* Warning text for accessed variant */}
        {isAccessed && (
          <div
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontStyle: "italic",
              fontWeight: 700,
              fontSize: isMobile ? "13px" : "16px",
              lineHeight: isMobile ? "150%" : "200%",
              color: "#010101",
              textAlign: "center",
              maxWidth: "595px",
              marginBottom: isMobile ? "16px" : "24px",
            }}
          >
            **Resetting a password or updating member credentials will automatically log the member out of their member dashboard.**
          </div>
        )}
        {/* Member Info Card - only for reset and resend variants */}
        {!isAccessed && (contactName || contactEmail) ? (
          <div
            style={{
              boxSizing: "border-box",
              border: "0.5px solid #000000",
              borderRadius: "10px",
              padding: isMobile ? "12px 46px 12px 12px" : "19px 44px 19px 24px",
              fontSize: isMobile ? "13px" : "16px",
              textAlign: "left",
              lineHeight: isMobile ? "20px" : "24px",
              marginBottom: isMobile ? "16px" : "20px",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              color: "#000000",
              width: isMobile ? "100%" : "416px",
              maxWidth: "100%",
              minHeight: isMobile ? "0" : "77px",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "3px",
              wordBreak: "break-word",
            }}
          >
            {contactName ? <div><span style={{ fontWeight: 700 }}>Contact:</span> {contactName}</div> : null}
            {contactEmail ? <div><span style={{ fontWeight: 700 }}>Email Username:</span> {contactEmail}</div> : null}
            <div
              style={{
                position: "absolute",
                top: "5px",
                right: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px 5px",
                backgroundColor: "#000000",
                borderRadius: "5px",
                width: "26px",
                height: "23px",
                cursor: "pointer",
              }}
              onClick={onEdit || onClose}
            >
              <img src={EditIcon} alt="Edit" style={{ width: "16px", height: "16px", filter: "brightness(0) invert(1)" }} />
            </div>
          </div>
        ) : null}
        {/* Confirmation text - only for reset and resend */}
        {!isAccessed && (
          <div
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 600,
              fontSize: isMobile ? "15px" : "18px",
              lineHeight: "150%",
              color: "#000000",
              marginBottom: isMobile ? "16px" : "24px",
            }}
          >
            {isResend ? "Are you sure you want to resend credentials?" : "Are you sure you want to continue?"}
          </div>
        )}
        {/* Buttons */}
        <div style={{ display: "flex", gap: isMobile ? "12px" : "28px", justifyContent: "center", flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
          {!isAccessed && (
            <Button
              style={{
                backgroundColor: isResend ? "#E1A731" : "#FFFFFF",
                color: isResend ? "#FFFFFF" : "#000",
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 600,
                fontSize: isMobile ? "14px" : "16px",
                lineHeight: "150%",
                borderRadius: "12px",
                padding: isMobile ? "12px 24px" : "15px 96px",
                textTransform: "uppercase",
                width: isMobile ? "100%" : "218px",
                height: isMobile ? "44px" : "50px",
                border: isResend ? "none" : "1px solid #000",
                boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              }}
              onClick={onSendEmail}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} style={{ color: isResend ? "#fff" : "#000" }} />
              ) : (
                isResend ? "RESEND" : "RESET"
              )}
            </Button>
          )}
          <Button
            style={{
              backgroundColor: "#000000",
              color: "#FFFFFF",
              fontFamily: "DM Sans, sans-serif",
              fontWeight: 600,
              fontSize: isMobile ? "14px" : "16px",
              lineHeight: "150%",
              borderRadius: "12px",
              padding: isMobile ? "12px 24px" : "15px 96px",
              textTransform: "uppercase",
              width: isMobile ? "100%" : "218px",
              height: isMobile ? "44px" : "50px",
              border: "1px solid #000000",
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
            onClick={onClose}
          >
            CANCEL
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResetDashboardPasswordDialog;
